import React, { useState, useMemo, useCallback } from 'react';
import { Calendar } from 'primereact/calendar';
import { MultiSelect } from 'primereact/multiselect';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Badge } from 'primereact/badge';
import { Divider } from 'primereact/divider';
import { ProgressBar } from 'primereact/progressbar';
import { SensorData, SensorReading } from '../../../types/SensorData';
import { fakeSensors } from '../../../types/SensorData';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import html2canvas from 'html2canvas';
import * as Highcharts from 'highcharts';

// Extension du type jsPDF pour inclure autoTable
declare module 'jspdf' {
  interface jsPDF {
    autoTable: (options: any) => jsPDF;
  }
}

// Types et interfaces
interface SensorReportGeneratorProps {
  onBack: () => void;
}

interface ExcursionData {
  id: string;
  sensorLabel: string;
  startTime: string;
  endTime: string;
  duration: number; // en minutes
  type: 'temperature' | 'humidity';
  threshold: 'min' | 'max';
  peakValue: number;
  averageValue: number;
  severity: 'low' | 'medium' | 'high';
}

interface ReportSummary {
  totalExcursions: number;
  temperatureExcursions: number;
  humidityExcursions: number;
  totalDuration: number;
  averageTemperature: number;
  averageHumidity: number;
  criticalExcursions: number;
  reportPeriod: string;
  sensorsCount: number;
}

const SensorReportGenerator: React.FC<SensorReportGeneratorProps> = ({ onBack }) => {
  const [selectedSensors, setSelectedSensors] = useState<SensorData[]>([]);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [reportData, setReportData] = useState<{
    excursions: ExcursionData[];
    summary: ReportSummary;
  } | null>(null);

  // Options pour le MultiSelect des capteurs
  const sensorOptions = useMemo(() => 
    fakeSensors.map(sensor => ({
      label: sensor.label,
      value: sensor,
      macAddr: sensor.macAddr
    })), []
  );

  // Fonction pour calculer la sévérité d'une excursion
  const calculateSeverity = useCallback((duration: number, deviation: number): 'low' | 'medium' | 'high' => {
    if (duration > 120 || Math.abs(deviation) > 10) return 'high';
    if (duration > 60 || Math.abs(deviation) > 5) return 'medium';
    return 'low';
  }, []);

  // Fonction simplifiée pour analyser les excursions
  const analyzeExcursions = useCallback((sensors: SensorData[], start: Date, end: Date): ExcursionData[] => {
    const excursions: ExcursionData[] = [];
    let excursionId = 1;
    
    sensors.forEach(sensor => {
      const filteredReadings = sensor.historique.filter(reading => {
        const readingDate = new Date(reading.timestamp);
        return readingDate >= start && readingDate <= end;
      });

      if (filteredReadings.length === 0) return;

      // Analyser les excursions de température
      for (let i = 0; i < filteredReadings.length; i++) {
        const reading = filteredReadings[i];
        const tempExceedsMin = reading.temperature < sensor.temperatureMin;
        const tempExceedsMax = reading.temperature > sensor.temperatureMax;
        
        if (tempExceedsMin || tempExceedsMax) {
          const startTime = reading.timestamp;
          let endTime = startTime;
          let peakValue = reading.temperature;
          let valueSum = reading.temperature;
          let valueCount = 1;
          
          // Chercher la fin de l'excursion
          for (let j = i + 1; j < filteredReadings.length; j++) {
            const nextReading = filteredReadings[j];
            const stillExceeds = tempExceedsMin 
              ? nextReading.temperature < sensor.temperatureMin
              : nextReading.temperature > sensor.temperatureMax;
            
            if (stillExceeds) {
              endTime = nextReading.timestamp;
              valueSum += nextReading.temperature;
              valueCount++;
              if (tempExceedsMin) {
                peakValue = Math.min(peakValue, nextReading.temperature);
              } else {
                peakValue = Math.max(peakValue, nextReading.temperature);
              }
              i = j; // Avancer l'index principal
            } else {
              break;
            }
          }
          
          const duration = (new Date(endTime).getTime() - new Date(startTime).getTime()) / (1000 * 60);
          const averageValue = valueSum / valueCount;
          const threshold = tempExceedsMin ? sensor.temperatureMin : sensor.temperatureMax;
          const deviation = Math.abs(peakValue - threshold);
          
          if (duration > 0) { // Seulement si l'excursion a une durée
            excursions.push({
              id: `temp-${excursionId++}`,
              sensorLabel: sensor.label,
              startTime,
              endTime,
              duration,
              type: 'temperature',
              threshold: tempExceedsMin ? 'min' : 'max',
              peakValue,
              averageValue,
              severity: calculateSeverity(duration, deviation)
            });
          }
        }
      }

      // Analyser les excursions d'humidité
      for (let i = 0; i < filteredReadings.length; i++) {
        const reading = filteredReadings[i];
        const humExceedsMin = reading.humidity < sensor.humidityMin;
        const humExceedsMax = reading.humidity > sensor.humidityMax;
        
        if (humExceedsMin || humExceedsMax) {
          const startTime = reading.timestamp;
          let endTime = startTime;
          let peakValue = reading.humidity;
          let valueSum = reading.humidity;
          let valueCount = 1;
          
          // Chercher la fin de l'excursion
          for (let j = i + 1; j < filteredReadings.length; j++) {
            const nextReading = filteredReadings[j];
            const stillExceeds = humExceedsMin 
              ? nextReading.humidity < sensor.humidityMin
              : nextReading.humidity > sensor.humidityMax;
            
            if (stillExceeds) {
              endTime = nextReading.timestamp;
              valueSum += nextReading.humidity;
              valueCount++;
              if (humExceedsMin) {
                peakValue = Math.min(peakValue, nextReading.humidity);
              } else {
                peakValue = Math.max(peakValue, nextReading.humidity);
              }
              i = j; // Avancer l'index principal
            } else {
              break;
            }
          }
          
          const duration = (new Date(endTime).getTime() - new Date(startTime).getTime()) / (1000 * 60);
          const averageValue = valueSum / valueCount;
          const threshold = humExceedsMin ? sensor.humidityMin : sensor.humidityMax;
          const deviation = Math.abs(peakValue - threshold);
          
          if (duration > 0) { // Seulement si l'excursion a une durée
            excursions.push({
              id: `hum-${excursionId++}`,
              sensorLabel: sensor.label,
              startTime,
              endTime,
              duration,
              type: 'humidity',
              threshold: humExceedsMin ? 'min' : 'max',
              peakValue,
              averageValue,
              severity: calculateSeverity(duration, deviation)
            });
          }
        }
      }
    });

    return excursions.sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
  }, [calculateSeverity]);

  // Fonction pour générer le résumé du rapport
  const generateSummary = useCallback((excursions: ExcursionData[], sensors: SensorData[], start: Date, end: Date): ReportSummary => {
    const tempExcursions = excursions.filter(e => e.type === 'temperature');
    const humExcursions = excursions.filter(e => e.type === 'humidity');
    const criticalExcursions = excursions.filter(e => e.severity === 'high');
    
    let totalTemp = 0;
    let totalHum = 0;
    let readingCount = 0;

    sensors.forEach(sensor => {
      sensor.historique
        .filter(reading => {
          const readingDate = new Date(reading.timestamp);
          return readingDate >= start && readingDate <= end;
        })
        .forEach(reading => {
          totalTemp += reading.temperature;
          totalHum += reading.humidity;
          readingCount++;
        });
    });

    const formatDate = (date: Date) => date.toLocaleDateString('fr-FR');
    const reportPeriod = `${formatDate(start)} - ${formatDate(end)}`;

    return {
      totalExcursions: excursions.length,
      temperatureExcursions: tempExcursions.length,
      humidityExcursions: humExcursions.length,
      totalDuration: excursions.reduce((sum, e) => sum + e.duration, 0),
      averageTemperature: readingCount > 0 ? totalTemp / readingCount : 0,
      averageHumidity: readingCount > 0 ? totalHum / readingCount : 0,
      criticalExcursions: criticalExcursions.length,
      reportPeriod,
      sensorsCount: sensors.length
    };
  }, []);

  // Fonction pour générer le rapport
  const generateReport = useCallback(async () => {
    if (!selectedSensors.length || !startDate || !endDate) {
      return;
    }

    setIsGenerating(true);
    
    try {
      // Simuler un délai de génération
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      const excursions = analyzeExcursions(selectedSensors, startDate, endDate);
      const summary = generateSummary(excursions, selectedSensors, startDate, endDate);
      
      setReportData({ excursions, summary });
    } catch (error) {
      console.error('Erreur lors de la génération du rapport:', error);
    } finally {
      setIsGenerating(false);
    }
  }, [selectedSensors, startDate, endDate, analyzeExcursions, generateSummary]);

  // Fonction pour créer un graphique Highcharts et le convertir en image
  const createHighchartsImage = useCallback(async (data: any[], type: 'temperature' | 'humidity', sensor: SensorData, startDate: Date, endDate: Date): Promise<string> => {
    return new Promise((resolve, reject) => {
      // Créer un conteneur temporaire pour le graphique
      const container = document.createElement('div');
      container.style.width = '800px';
      container.style.height = '300px';
      container.style.position = 'absolute';
      container.style.left = '-9999px';
      container.style.top = '-9999px';
      document.body.appendChild(container);

      const chartData = data.map(item => [new Date(item.timestamp).getTime(), type === 'temperature' ? item.temperature : item.humidity]);
      
      const minThreshold = type === 'temperature' ? (sensor as any).temperatureMin : (sensor as any).humidityMin;
      const maxThreshold = type === 'temperature' ? (sensor as any).temperatureMax : (sensor as any).humidityMax;
      const unit = type === 'temperature' ? '°C' : '%';
      const color = type === 'temperature' ? '#ef4444' : '#3b82f6';
      
      const chart = Highcharts.chart(container, {
        chart: {
          type: 'line',
          backgroundColor: '#ffffff',
          width: 800,
          height: 300
        },
        title: {
          text: `${type === 'temperature' ? 'Température' : 'Humidité'} - ${sensor.label}`,
          style: {
            fontSize: '16px',
            fontWeight: 'bold'
          }
        },
        subtitle: {
          text: `Période: ${startDate.toLocaleDateString('fr-FR')} - ${endDate.toLocaleDateString('fr-FR')}`
        },
        xAxis: {
          type: 'datetime',
          title: {
            text: 'Temps'
          },
          dateTimeLabelFormats: {
            day: '%d/%m',
            hour: '%H:%M'
          }
        },
        yAxis: {
          title: {
            text: `${type === 'temperature' ? 'Température' : 'Humidité'} (${unit})`
          },
          plotLines: [
            ...(minThreshold !== undefined ? [{
              color: '#3b82f6',
              dashStyle: 'Dash' as const,
              value: minThreshold,
              width: 2,
              label: {
                text: `Seuil Min: ${minThreshold}${unit}`,
                align: 'right' as const
              }
            }] : []),
            ...(maxThreshold !== undefined ? [{
              color: '#ef4444',
              dashStyle: 'Dash' as const,
              value: maxThreshold,
              width: 2,
              label: {
                text: `Seuil Max: ${maxThreshold}${unit}`,
                align: 'right' as const
              }
            }] : [])
          ]
        },
        legend: {
          enabled: false
        },
        plotOptions: {
          line: {
            marker: {
              enabled: false,
              states: {
                hover: {
                  enabled: true,
                  radius: 4
                }
              }
            },
            lineWidth: 2
          }
        },
        series: [{
          name: type === 'temperature' ? 'Température' : 'Humidité',
          data: chartData,
          color: color,
          type: 'line'
        }],
        tooltip: {
          formatter: function() {
            return `<b>${this.series.name}</b><br/>${Highcharts.dateFormat('%d/%m/%Y %H:%M', this.x!)}: ${this.y}${unit}`;
          }
        },
        credits: {
          enabled: false
        }
      });

      // Attendre que le graphique soit rendu puis le convertir en image
      setTimeout(async () => {
        try {
          const canvas = await html2canvas(container, {
            backgroundColor: '#ffffff',
            scale: 2,
            useCORS: true
          });
          
          const imageDataUrl = canvas.toDataURL('image/png');
          
          // Nettoyer
          chart.destroy();
          document.body.removeChild(container);
          
          resolve(imageDataUrl);
        } catch (error) {
          console.error('Erreur lors de la capture du graphique:', error);
          document.body.removeChild(container);
          reject(error);
        }
      }, 1000);
    });
  }, []);

  // Fonction pour charger une image depuis une URL
  const loadImageFromUrl = useCallback((url: string): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      // Essayer sans CORS d'abord, puis avec si nécessaire
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Cannot get canvas context'));
            return;
          }
          canvas.width = img.width;
          canvas.height = img.height;
          ctx.drawImage(img, 0, 0);
          const dataUrl = canvas.toDataURL('image/png');
          console.log('Logo chargé avec succès, taille:', dataUrl.length);
          resolve(dataUrl);
        } catch (error) {
          console.error('Erreur lors du traitement de l\'image:', error);
          reject(error);
        }
      };
      img.onerror = (error) => {
        console.error('Erreur de chargement de l\'image:', error);
        reject(new Error('Failed to load image'));
      };
      img.src = url;
    });
  }, []);

  // Fonction pour créer un logo de remplacement simple
  const createFallbackLogo = useCallback((): string => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return '';
    
    canvas.width = 100;
    canvas.height = 80;
    
    // Fond blanc
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, 100, 80);
    
    // Bordure bleue
    ctx.strokeStyle = '#2980b9';
    ctx.lineWidth = 2;
    ctx.strokeRect(2, 2, 96, 76);
    
    // Texte OMNIYAT
    ctx.fillStyle = '#2980b9';
    ctx.font = 'bold 12px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('OMNIYAT', 50, 30);
    
    // Sous-titre
    ctx.font = '8px Arial';
    ctx.fillText('LOGITEMP', 50, 50);
    ctx.fillText('REPORT', 50, 62);
    
    return canvas.toDataURL('image/png');
  }, []);

  // Fonction pour exporter en PDF avec design professionnel
  const exportToPDF = useCallback(async () => {
    if (!reportData || !startDate || !endDate) return;

    try {
      // Charger le logo Omniyat
      const logoUrl = 'https://www.omniyat.ma/wp-content/uploads/2021/03/logo.png';
      let logoDataUrl: string | null = null;
      try {
        console.log('Tentative de chargement du logo Omniyat...');
        logoDataUrl = await loadImageFromUrl(logoUrl);
        console.log('Logo chargé avec succès!');
      } catch (error) {
        console.warn('Impossible de charger le logo Omniyat depuis l\'URL externe:', error);
        // Créer un logo de remplacement simple
        logoDataUrl = createFallbackLogo();
        console.log('Logo de remplacement créé');
      }
      
      // Créer PDF en format portrait
      const pdf = new jsPDF('portrait', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      
      // Grouper les excursions par capteur
      const excursionsBySensor = selectedSensors.reduce((acc, sensor) => {
        const sensorExcursions = reportData.excursions.filter(exc => exc.sensorLabel === sensor.label);
        
        // Top 5 température et top 5 humidité
        const tempExcursions = sensorExcursions
          .filter(exc => exc.type === 'temperature')
          .sort((a, b) => b.duration - a.duration)
          .slice(0, 5);
        
        const humExcursions = sensorExcursions
          .filter(exc => exc.type === 'humidity')
          .sort((a, b) => b.duration - a.duration)
          .slice(0, 5);
        
        acc[sensor.macAddr] = {
          sensor,
          tempExcursions,
          humExcursions,
          totalExcursions: sensorExcursions.length,
          totalDuration: sensorExcursions.reduce((sum, exc) => sum + exc.duration, 0)
        };
        
        return acc;
      }, {} as Record<string, any>);

      let pageCount = 0;
      
      // Créer une page pour chaque capteur
      const sensorDataArray = Object.values(excursionsBySensor);
      for (let index = 0; index < sensorDataArray.length; index++) {
        const sensorData: any = sensorDataArray[index];
        if (index > 0) pdf.addPage('a4', 'portrait');
        pageCount++;
        
        const { sensor, tempExcursions, humExcursions, totalExcursions, totalDuration } = sensorData;
        
        // Recalculer les totaux réels pour ce capteur
        const sensorExcursions = reportData.excursions.filter(exc => exc.sensorLabel === sensor.label);
        const totalTempExcursions = sensorExcursions.filter(exc => exc.type === 'temperature').length;
        const totalHumExcursions = sensorExcursions.filter(exc => exc.type === 'humidity').length;
        
        // === EN-TÊTE AVEC LOGO ===
        pdf.setFillColor(41, 128, 185);
        pdf.rect(0, 0, pageWidth, 30, 'F'); // Hauteur augmentée pour le logo
        
        // Ajouter le logo Omniyat si disponible
        if (logoDataUrl) {
          try {
            const logoWidth = 25;
            const logoHeight = 20;
            const logoX = 15;
            const logoY = 5;
            pdf.addImage(logoDataUrl, 'PNG', logoX, logoY, logoWidth, logoHeight);
          } catch (error) {
            console.warn('Erreur lors de l\'ajout du logo:', error);
          }
        }
        
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        const titleX = logoDataUrl ? 50 : 20; // Décaler le titre si logo présent
        pdf.text(`Rapport d'Analyse - ${sensor.label}`, titleX, 18);
        
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`Généré le ${new Date().toLocaleDateString('fr-FR')} | Page ${pageCount}`, pageWidth - 80, 22);
        
        // === CARDS D'INFORMATIONS ===
        pdf.setTextColor(0, 0, 0);
        let yPos = 45; // Ajusté pour tenir compte de l'en-tête plus haute
        
        // Card 1: Informations du capteur (ajusté pour A4 portrait)
        const cardWidth = (pageWidth - 60) / 3; // 3 cartes avec marges
        const cardHeight = 35;
        const cardSpacing = 10;
        
        pdf.setFillColor(248, 249, 250);
        pdf.roundedRect(20, yPos, cardWidth, cardHeight, 3, 3, 'F');
        pdf.setDrawColor(200, 200, 200);
        pdf.roundedRect(20, yPos, cardWidth, cardHeight, 3, 3, 'S');
        
        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Informations Capteur', 25, yPos + 8);
        
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`MAC: ${sensor.macAddr}`, 25, yPos + 16);
        pdf.text(`Type: ${'temperature' in sensor ? 'Temp/Hum' : 'Porte'}`, 25, yPos + 22);
        pdf.text(`Batterie: ${sensor.battery}%`, 25, yPos + 28);
        
        // Card 2: Statistiques d'excursions
        const card2X = 20 + cardWidth + cardSpacing;
        pdf.setFillColor(254, 242, 242);
        pdf.roundedRect(card2X, yPos, cardWidth, cardHeight, 3, 3, 'F');
        pdf.setDrawColor(200, 200, 200);
        pdf.roundedRect(card2X, yPos, cardWidth, cardHeight, 3, 3, 'S');
        
        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Excursions Totales', card2X + 5, yPos + 8);
        
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`Nombre: ${totalExcursions}`, card2X + 5, yPos + 16);
        pdf.text(`Température: ${totalTempExcursions}`, card2X + 5, yPos + 22);
        pdf.text(`Humidité: ${totalHumExcursions}`, card2X + 5, yPos + 28);
        
        // Card 3: Durée totale
        const card3X = card2X + cardWidth + cardSpacing;
        pdf.setFillColor(240, 253, 244);
        pdf.roundedRect(card3X, yPos, cardWidth, cardHeight, 3, 3, 'F');
        pdf.setDrawColor(200, 200, 200);
        pdf.roundedRect(card3X, yPos, cardWidth, cardHeight, 3, 3, 'S');
        
        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Durée Cumulée', card3X + 5, yPos + 8);
        
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(34, 197, 94);
        const hours = Math.floor(totalDuration / 60);
        const minutes = Math.round(totalDuration % 60);
        pdf.text(`${hours}h ${minutes}m`, card3X + 5, yPos + 22);
        
        pdf.setTextColor(0, 0, 0);
        
        // === TABLEAU DES TOP EXCURSIONS ===
        yPos = 85;
        
        // Déterminer le titre selon les types d'excursions disponibles
        let excursionsTitle = 'Top Excursions';
        if (totalTempExcursions > 0 && totalHumExcursions > 0) {
          excursionsTitle = `Top ${Math.min(10, totalTempExcursions + totalHumExcursions)} Excursions (Température + Humidité)`;
        } else if (totalTempExcursions > 0) {
          excursionsTitle = `Top ${Math.min(5, totalTempExcursions)} Excursions de Température`;
        } else if (totalHumExcursions > 0) {
          excursionsTitle = `Top ${Math.min(5, totalHumExcursions)} Excursions d'Humidité`;
        } else {
          excursionsTitle = 'Aucune excursion détectée';
        }
        
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.text(excursionsTitle, 20, yPos);
        
        const top10Excursions = [...tempExcursions, ...humExcursions]
          .sort((a, b) => b.duration - a.duration);
        
        if (top10Excursions.length > 0) {
          const tableData = top10Excursions.map((exc, idx) => [
            (idx + 1).toString(),
            exc.type === 'temperature' ? 'Température' : 'Humidité',
            exc.threshold === 'min' ? 'Minimum' : 'Maximum',
            new Date(exc.startTime).toLocaleDateString('fr-FR'),
            new Date(exc.startTime).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
            `${Math.floor(exc.duration / 60)}h ${Math.round(exc.duration % 60)}m`,
            `${exc.peakValue.toFixed(1)}${exc.type === 'temperature' ? '°C' : '%'}`,
            exc.severity === 'high' ? 'Élevé' : exc.severity === 'medium' ? 'Moyen' : 'Faible'
          ]);
          
          pdf.autoTable({
            head: [['#', 'Type', 'Seuil', 'Date', 'Heure', 'Durée', 'Valeur Pic', 'Sévérité']],
            body: tableData,
            startY: yPos + 5,
            styles: { 
              fontSize: 8,
              cellPadding: 2,
              halign: 'center'
            },
            headStyles: { 
              fillColor: [41, 128, 185],
              textColor: [255, 255, 255],
              fontStyle: 'bold'
            },
            alternateRowStyles: {
              fillColor: [248, 249, 250]
            },
            columnStyles: {
              0: { cellWidth: 12 },  // # - plus compact
              1: { cellWidth: 25 },  // Type - réduit
              2: { cellWidth: 20 },  // Seuil - réduit
              3: { cellWidth: 25 },  // Date - réduit
              4: { cellWidth: 20 },  // Heure - réduit
              5: { cellWidth: 25 },  // Durée - réduit
              6: { cellWidth: 25 },  // Valeur Pic - réduit
              7: { cellWidth: 18 }   // Sévérité - plus compact
            },
            margin: { left: 20, right: 20 }, // Marges pour centrer le tableau
            tableWidth: 'wrap' // Ajustement automatique
          });
        }
        
        // === GRAPHIQUES (Simulation avec des barres) ===
        yPos = 140;
        
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Analyse des Tendances', 20, yPos);
        
        if ('temperature' in sensor && sensor.historique) {
          // Filtrer les données sur la période sélectionnée
          const periodData = sensor.historique.filter((h: any) => {
            const readingDate = new Date(h.timestamp);
            return readingDate >= startDate && readingDate <= endDate;
          }).sort((a: any, b: any) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
          
          if (periodData.length > 0) {
            // === GRAPHIQUES HIGHCHARTS ===
            yPos += 10;
            
            try {
              // Générer le graphique de température
              const tempImageUrl = await createHighchartsImage(periodData, 'temperature', sensor, startDate, endDate);
              
              // Ajouter l'image de température au PDF (ajusté pour A4 portrait)
              const tempImgWidth = pageWidth - 40; // Largeur adaptée avec marges
              const tempImgHeight = 60; // Hauteur réduite pour économiser l'espace
              pdf.addImage(tempImageUrl, 'PNG', 20, yPos, tempImgWidth, tempImgHeight);
              
              yPos += tempImgHeight + 10;
              
              // Générer le graphique d'humidité
              const humImageUrl = await createHighchartsImage(periodData, 'humidity', sensor, startDate, endDate);
              
              // Ajouter l'image d'humidité au PDF (même dimensions)
              pdf.addImage(humImageUrl, 'PNG', 20, yPos, tempImgWidth, tempImgHeight);
              
              yPos += tempImgHeight + 5;
              
              // Statistiques résumées
              const temps = periodData.map((h: any) => h.temperature);
              const hums = periodData.map((h: any) => h.humidity);
              
              const tempMin = Math.min(...temps);
              const tempMax = Math.max(...temps);
              const tempAvg = temps.reduce((a: number, b: number) => a + b, 0) / temps.length;
              
              const humMin = Math.min(...hums);
              const humMax = Math.max(...hums);
              const humAvg = hums.reduce((a: number, b: number) => a + b, 0) / hums.length;
              
              // Légendes statistiques (espacement réduit)
              yPos += 10; // Espacement avant les statistiques
              pdf.setFontSize(8);
              pdf.setTextColor(100, 100, 100);
              pdf.text(`Statistiques - Température: Min ${tempMin.toFixed(1)}°C, Moy ${tempAvg.toFixed(1)}°C, Max ${tempMax.toFixed(1)}°C`, 20, yPos);
              pdf.text(`Statistiques - Humidité: Min ${humMin.toFixed(1)}%, Moy ${humAvg.toFixed(1)}%, Max ${humMax.toFixed(1)}%`, 20, yPos + 6);
              pdf.text(`Nombre de points de données: ${periodData.length}`, 20, yPos + 12);
              yPos += 20; // Espacement après les statistiques
              
            } catch (error) {
              console.error('Erreur lors de la génération des graphiques:', error);
              // Fallback: message d'erreur
              pdf.setFontSize(10);
              pdf.setTextColor(255, 0, 0);
              pdf.text('Erreur lors de la génération des graphiques', 20, yPos);
              yPos += 20;
            }
            
          } else {
            // Aucune donnée pour la période
            yPos += 15;
            pdf.setFontSize(10);
            pdf.setTextColor(128, 128, 128);
            pdf.text(`Aucune donnée disponible pour la période sélectionnée`, 20, yPos);
          }
        } else {
          // Capteur de porte - pas de graphiques temp/hum
          yPos += 15;
          pdf.setFontSize(10);
          pdf.setTextColor(128, 128, 128);
          pdf.text('Capteur de porte - Courbes température/humidité non applicables', 20, yPos);
        }
        
        // === PIED DE PAGE ===
        pdf.setFontSize(8);
        pdf.setTextColor(128, 128, 128);
        pdf.text(`Rapport généré automatiquement - ${reportData.summary.reportPeriod}`, 20, pageHeight - 10);
      }
      
      // Sauvegarder le PDF
      pdf.save(`rapport-capteurs-detaille-${new Date().toISOString().split('T')[0]}.pdf`);
      
    } catch (error) {
      console.error('Erreur lors de l\'export PDF:', error);
    }
  }, [reportData, selectedSensors]);

  // Fonction pour exporter en CSV
  const exportToCSV = useCallback(() => {
    if (!reportData) return;

    try {
      const headers = ['Capteur', 'Type', 'Seuil', 'Début', 'Fin', 'Durée (min)', 'Valeur Pic', 'Valeur Moyenne', 'Sévérité'];
      const csvData = reportData.excursions.map(excursion => [
        excursion.sensorLabel,
        excursion.type,
        excursion.threshold,
        excursion.startTime,
        excursion.endTime,
        excursion.duration.toFixed(1),
        excursion.peakValue.toFixed(2),
        excursion.averageValue.toFixed(2),
        excursion.severity
      ]);

      const csvContent = [headers, ...csvData]
        .map(row => row.map(cell => `"${cell}"`).join(','))
        .join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `rapport-excursions-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Erreur lors de l\'export CSV:', error);
    }
  }, [reportData]);

  // Templates pour le DataTable
  const severityTemplate = (excursion: ExcursionData) => {
    const severityConfig = {
      low: { label: 'Faible', severity: 'success' as const },
      medium: { label: 'Moyen', severity: 'warning' as const },
      high: { label: 'Élevé', severity: 'danger' as const }
    };
    
    const config = severityConfig[excursion.severity];
    return <Badge value={config.label} severity={config.severity} />;
  };

  const typeTemplate = (excursion: ExcursionData) => {
    return (
      <span className={`p-tag ${
        excursion.type === 'temperature' ? 'p-tag-warning' : 'p-tag-info'
      }`}>
        {excursion.type === 'temperature' ? 'Température' : 'Humidité'}
      </span>
    );
  };

  const durationTemplate = (excursion: ExcursionData) => {
    const hours = Math.floor(excursion.duration / 60);
    const minutes = Math.round(excursion.duration % 60);
    return `${hours}h ${minutes}m`;
  };

  return (
    <div className="sensor-report-generator">
      <div className="flex justify-content-between align-items-center mb-4">
        <h2>Générateur de Rapports d'Excursions</h2>
        <Button 
          icon="pi pi-arrow-left" 
          label="Retour" 
          onClick={onBack}
          className="p-button-outlined"
        />
      </div>

      <Card className="mb-4">
        <div className="grid">
          <div className="col-12 md:col-4">
            <label htmlFor="sensors" className="block text-900 font-medium mb-2">
              Capteurs à analyser
            </label>
            <MultiSelect
              id="sensors"
              value={selectedSensors}
              onChange={(e) => setSelectedSensors(e.value)}
              options={sensorOptions}
              optionLabel="label"
              placeholder="Sélectionner des capteurs"
              className="w-full"
              maxSelectedLabels={3}
              showSelectAll={false}
            />
          </div>
          
          <div className="col-12 md:col-3">
            <label htmlFor="startDate" className="block text-900 font-medium mb-2">
              Date de début
            </label>
            <Calendar
              id="startDate"
              value={startDate}
              onChange={(e) => {
                const value = e.value;
                if (value instanceof Date) {
                  setStartDate(value);
                } else if (value === null) {
                  setStartDate(null);
                }
                // Ignore other types (string, Date[], undefined)
              }}
              showIcon
              className="w-full"
              dateFormat="dd/mm/yy"
              maxDate={endDate || new Date()}
            />
          </div>
          
          <div className="col-12 md:col-3">
            <label htmlFor="endDate" className="block text-900 font-medium mb-2">
              Date de fin
            </label>
            <Calendar
              id="endDate"
              value={endDate}
              onChange={(e) => {
                const value = e.value;
                if (value instanceof Date) {
                  setEndDate(value);
                } else if (value === null) {
                  setEndDate(null);
                }
                // Ignore other types (string, Date[], undefined)
              }}
              showIcon
              className="w-full"
              dateFormat="dd/mm/yy"
              minDate={startDate || undefined}
              maxDate={new Date()}
            />
          </div>
          
          <div className="col-12 md:col-2">
            <label className="block text-900 font-medium mb-2">&nbsp;</label>
            <Button
              label="Analyser"
              icon="pi pi-search"
              onClick={generateReport}
              disabled={!selectedSensors.length || !startDate || !endDate || isGenerating}
              loading={isGenerating}
              className="w-full"
            />
          </div>
        </div>
        
        {isGenerating && (
          <div className="mt-3">
            <ProgressBar mode="indeterminate" style={{ height: '6px' }} />
            <p className="text-center mt-2 text-600">Analyse des données en cours...</p>
          </div>
        )}
      </Card>

      {reportData && (
        <>
          {/* Résumé du rapport */}
          <Card className="mb-4">
            <div className="flex justify-content-between align-items-center mb-3">
              <h3>Résumé du Rapport</h3>
              <div className="flex gap-2">
                <Button
                  label="Export PDF"
                  icon="pi pi-file-pdf"
                  onClick={exportToPDF}
                  className="p-button-outlined p-button-danger"
                  size="small"
                />
                <Button
                  label="Export CSV"
                  icon="pi pi-file-excel"
                  onClick={exportToCSV}
                  className="p-button-outlined p-button-success"
                  size="small"
                />
              </div>
            </div>
            
            <div className="grid">
              <div className="col-6 md:col-3">
                <div className="text-center p-3 border-round surface-100">
                  <div className="text-2xl font-bold text-primary">{reportData.summary.totalExcursions}</div>
                  <div className="text-600">Total Excursions</div>
                </div>
              </div>
              <div className="col-6 md:col-3">
                <div className="text-center p-3 border-round surface-100">
                  <div className="text-2xl font-bold text-orange-500">{reportData.summary.temperatureExcursions}</div>
                  <div className="text-600">Température</div>
                </div>
              </div>
              <div className="col-6 md:col-3">
                <div className="text-center p-3 border-round surface-100">
                  <div className="text-2xl font-bold text-blue-500">{reportData.summary.humidityExcursions}</div>
                  <div className="text-600">Humidité</div>
                </div>
              </div>
              <div className="col-6 md:col-3">
                <div className="text-center p-3 border-round surface-100">
                  <div className="text-2xl font-bold text-red-500">{reportData.summary.criticalExcursions}</div>
                  <div className="text-600">Critiques</div>
                </div>
              </div>
            </div>
            
            <Divider />
            
            <div className="grid">
              <div className="col-6 md:col-4">
                <div className="text-center">
                  <div className="text-lg font-semibold">{reportData.summary.averageTemperature.toFixed(1)}°C</div>
                  <div className="text-600">Temp. Moyenne</div>
                </div>
              </div>
              <div className="col-6 md:col-4">
                <div className="text-center">
                  <div className="text-lg font-semibold">{reportData.summary.averageHumidity.toFixed(1)}%</div>
                  <div className="text-600">Hum. Moyenne</div>
                </div>
              </div>
              <div className="col-12 md:col-4">
                <div className="text-center">
                  <div className="text-lg font-semibold">{Math.round(reportData.summary.totalDuration / 60)}h {Math.round(reportData.summary.totalDuration % 60)}m</div>
                  <div className="text-600">Durée Totale</div>
                </div>
              </div>
            </div>
          </Card>

          {/* Tableau des excursions */}
          <Card>
            <h3>Détail des Excursions ({reportData.excursions.length})</h3>
            {reportData.excursions.length > 0 ? (
              <DataTable 
                value={reportData.excursions} 
                paginator 
                rows={10}
                className="p-datatable-sm"
                sortField="startTime"
                sortOrder={1}
                responsiveLayout="scroll"
              >
                <Column field="sensorLabel" header="Capteur" sortable style={{ minWidth: '120px' }} />
                <Column body={typeTemplate} header="Type" sortable field="type" style={{ minWidth: '100px' }} />
                <Column 
                  field="threshold" 
                  header="Seuil" 
                  sortable 
                  style={{ minWidth: '80px' }}
                  body={(excursion) => excursion.threshold === 'min' ? 'Min' : 'Max'}
                />
                <Column 
                  field="startTime" 
                  header="Début" 
                  sortable 
                  style={{ minWidth: '140px' }}
                  body={(excursion) => new Date(excursion.startTime).toLocaleString('fr-FR')}
                />
                <Column 
                  body={durationTemplate} 
                  header="Durée" 
                  sortable 
                  field="duration" 
                  style={{ minWidth: '80px' }}
                />
                <Column 
                  field="peakValue" 
                  header="Valeur Pic" 
                  sortable 
                  style={{ minWidth: '100px' }}
                  body={(excursion) => `${excursion.peakValue.toFixed(1)}${excursion.type === 'temperature' ? '°C' : '%'}`}
                />
                <Column 
                  body={severityTemplate} 
                  header="Sévérité" 
                  sortable 
                  field="severity" 
                  style={{ minWidth: '100px' }}
                />
              </DataTable>
            ) : (
              <div className="text-center p-4">
                <i className="pi pi-check-circle text-4xl text-green-500 mb-3"></i>
                <p className="text-xl font-medium">Aucune excursion détectée</p>
                <p className="text-600">Tous les capteurs sont restés dans leurs plages normales pendant la période analysée.</p>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
};

export default SensorReportGenerator;
